import { type HarRequest, HTTPSnippet, type TargetId } from "httpsnippet-lite";

import { HTTP_SNIPPET_CLIENTS } from "./constants";

const IMPORT_PREFIXES: Partial<Record<TargetId, string>> = {
    csharp: "using RestSharp;\n\n",
    java: "import com.mashape.unirest.http.HttpResponse;\nimport com.mashape.unirest.http.Unirest;\n\n"
};

const PHP_AUTOLOAD_INSERT = "\nrequire_once('vendor/autoload.php');\n";

export interface BasicAuthInfo {
    username: string;
    password: string;
}

export class HttpSnippetConverter {
    private readonly snippet: HTTPSnippet;
    private readonly basicAuth: BasicAuthInfo | undefined;

    constructor(harRequest: HarRequest, basicAuth?: BasicAuthInfo) {
        this.snippet = new HTTPSnippet(harRequest);
        this.basicAuth = basicAuth;
    }

    async convert(targetId: TargetId, clientId: string): Promise<string | undefined> {
        const convertedCode = await this.snippet.convert(targetId, clientId);
        const raw =
            typeof convertedCode === "string" ? convertedCode : convertedCode != null ? convertedCode[0] : undefined;

        if (raw == null) {
            return undefined;
        }

        let code = this.addImports(raw, targetId);
        if (this.basicAuth != null) {
            code = applyBasicAuthBase64(code, targetId, this.basicAuth);
        }
        return code;
    }

    async convertAll(): Promise<{ targetId: TargetId; code: string }[]> {
        const results: { targetId: TargetId; code: string }[] = [];

        for (const { targetId, clientId } of HTTP_SNIPPET_CLIENTS) {
            const code = await this.convert(targetId, clientId);
            if (code != null) {
                results.push({ targetId, code });
            }
        }

        return results;
    }

    private addImports(code: string, targetId: TargetId): string {
        if (targetId === "php") {
            return code.replace(/^<\?php\n/, `<?php${PHP_AUTOLOAD_INSERT}`);
        }

        const prefix = IMPORT_PREFIXES[targetId];
        if (prefix != null) {
            return `${prefix}${code}`;
        }

        return code;
    }
}

/**
 * Replace the literal "Basic <username>:<password>" header value with
 * idiomatic base64 encoding code for each target language.
 */
function applyBasicAuthBase64(code: string, targetId: TargetId, auth: BasicAuthInfo): string {
    const literalValue = `Basic ${auth.username}:${auth.password}`;

    // Build a regex that matches the literal value in single or double quotes
    const escapedLiteral = literalValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const quotedPattern = new RegExp(`(["'])${escapedLiteral}\\1`);

    if (!quotedPattern.test(code)) {
        return code;
    }

    switch (targetId) {
        case "python":
            return applyPythonBasicAuth(code, quotedPattern, auth);
        case "javascript":
            return applyJavaScriptBasicAuth(code, quotedPattern, auth);
        case "go":
            return applyGoBasicAuth(code, auth);
        case "ruby":
            return applyRubyBasicAuth(code, quotedPattern, auth);
        case "java":
            return applyJavaBasicAuth(code, quotedPattern, auth);
        case "php":
            return applyPhpBasicAuth(code, quotedPattern, auth);
        case "csharp":
            return applyCSharpBasicAuth(code, quotedPattern, auth);
        case "swift":
            return applySwiftBasicAuth(code, quotedPattern, auth);
        default:
            return code;
    }
}

function applyPythonBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    const importLine = "import base64\n";
    const credentialsVar = `credentials = base64.b64encode(f"${auth.username}:${auth.password}".encode()).decode()\n`;

    // Add imports at the top
    code = code.replace(/^(import requests\n)/, `$1${importLine}`);

    // Add credentials variable before the headers dict
    code = code.replace(/^(headers = \{)/m, `${credentialsVar}\n$1`);

    // Replace the literal with the variable reference
    code = code.replace(pattern, `f"Basic {credentials}"`);

    return code;
}

function applyJavaScriptBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    const credentialsLine = `const credentials = btoa("${auth.username}:${auth.password}");\n`;

    // Add credentials variable before the options
    code = code.replace(/^(const options)/m, `${credentialsLine}\n$1`);

    // Replace the literal with template literal
    code = code.replace(pattern, "`Basic ${credentials}`");

    return code;
}

function applyGoBasicAuth(code: string, auth: BasicAuthInfo): string {
    // Go has a built-in SetBasicAuth method on http.Request
    // Replace Header.Add("Authorization", "Basic ...") with req.SetBasicAuth(username, password)
    const headerPattern = new RegExp(
        `req\\.Header\\.Add\\("Authorization",\\s*"Basic ${auth.username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}:${auth.password.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}"\\)`
    );
    code = code.replace(headerPattern, `req.SetBasicAuth("${auth.username}", "${auth.password}")`);

    return code;
}

function applyRubyBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    const requireLine = "require 'base64'\n";

    // Add require at the top
    code = code.replace(/^(require 'uri'\n)/, `$1${requireLine}`);

    // Replace the literal with Base64 encoding
    code = code.replace(pattern, `"Basic #{Base64.strict_encode64("${auth.username}:${auth.password}")}"`);

    return code;
}

function applyJavaBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    // Add import at the top
    const importLine = "import java.util.Base64;\n";
    code = code.replace(/^(import com\.mashape)/m, `${importLine}$1`);

    // Replace the literal with Base64 encoding
    code = code.replace(
        pattern,
        `"Basic " + Base64.getEncoder().encodeToString("${auth.username}:${auth.password}".getBytes())`
    );

    return code;
}

function applyPhpBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    code = code.replace(pattern, `'Basic ' . base64_encode('${auth.username}:${auth.password}')`);
    return code;
}

function applyCSharpBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    const usingLine = "using System;\nusing System.Text;\n";
    code = code.replace(/^(using RestSharp;\n)/, `$1${usingLine}`);

    code = code.replace(
        pattern,
        `"Basic " + Convert.ToBase64String(Encoding.UTF8.GetBytes("${auth.username}:${auth.password}"))`
    );

    return code;
}

function applySwiftBasicAuth(code: string, pattern: RegExp, auth: BasicAuthInfo): string {
    // Add credentials variable after import Foundation
    const credentialsCode = `\nlet credentials = Data("${auth.username}:${auth.password}".utf8).base64EncodedString()\n`;
    code = code.replace(/^(import Foundation\n)/, `$1${credentialsCode}`);

    // Replace the literal with string interpolation
    code = code.replace(pattern, `"Basic \\(credentials)"`);

    return code;
}
